<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ChannelSelector from '../ChannelSelector.vue';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['channelItemClick']);
const { t } = useI18n();

const hasFbConfigured = computed(() => {
  return window.nuriemangoConfig?.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  return window.nuriemangoConfig?.instagramAppId;
});

const hasTiktokConfigured = computed(() => {
  return window.nuriemangoConfig?.tiktokAppId;
});

const enabledFeatures = computed(() => props.enabledFeatures || {});

const accountFeatureKeyMap = {
  website: 'channel_website',
  facebook: 'channel_facebook',
  email: 'channel_email',
  instagram: 'channel_instagram',
  tiktok: 'channel_tiktok',
  kakao: 'channel_kakao',
};

const channelRequiresAdminConfig = key => {
  if (key === 'facebook') return !hasFbConfigured.value;
  if (key === 'instagram') return !hasInstagramConfigured.value;
  if (key === 'tiktok') return !hasTiktokConfigured.value;
  return false;
};

const isFeatureEnabled = computed(() => {
  const featureKey = accountFeatureKeyMap[props.channel.key];
  if (!featureKey) return true;
  return !!enabledFeatures.value[featureKey];
});

const isActive = computed(() => {
  const { key } = props.channel;
  if (Object.keys(enabledFeatures.value).length === 0) {
    return false;
  }
  if (key === 'website') {
    return enabledFeatures.value.channel_website;
  }
  if (key === 'facebook') {
    return enabledFeatures.value.channel_facebook && hasFbConfigured.value;
  }
  if (key === 'email') {
    return enabledFeatures.value.channel_email;
  }

  if (key === 'instagram') {
    return (
      enabledFeatures.value.channel_instagram && hasInstagramConfigured.value
    );
  }

  if (key === 'tiktok') {
    return enabledFeatures.value.channel_tiktok && hasTiktokConfigured.value;
  }

  if (key === 'kakao') {
    return enabledFeatures.value.channel_kakao;
  }

  return [
    'website',
    'twilio',
    'api',
    'whatsapp',
    'sms',
    'telegram',
    'line',
    'instagram',
    'tiktok',
  ].includes(key);
});

const isComingSoon = computed(() => false);

const disabledText = computed(() => {
  const { key, title } = props.channel;
  if (isActive.value || isComingSoon.value) {
    return '';
  }

  if (!isFeatureEnabled.value) {
    return t('CHANNEL_SELECTOR.FEATURE_DISABLED');
  }

  if (channelRequiresAdminConfig(key)) {
    return t('CHANNEL_SELECTOR.ADMIN_CONFIG_REQUIRED', {
      channel: title,
    });
  }

  return '';
});

const onItemClick = () => {
  if (isActive.value) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :disabled-text="disabledText"
    :disabled="!isActive"
    @click="onItemClick"
  />
</template>
